<script setup lang="ts">
  import { computed } from 'vue'
  import { Button } from '@/components/ui/button'
  import { Loader2 } from 'lucide-vue-next'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'

  const themeColor = computed(() => useThemeColor().value)

  const { t } = useTranslation({
    en: {
      submitDocument: 'Submit Document',
      submitQuestionnaire: 'Submit Questionnaire',
      submitDocumentDescription:
        'Do you want to submit this document? This action cannot be undone.',
      submitQuestionnaireDescription:
        'Do you want to submit the questionnaire now? After submitting, you cannot change your answers.',
      cancel: 'Cancel',
      yesSubmit: 'Yes, submit',
      warning: 'Warning',
      incompleteWarning: 'You have not yet answered all questions.'
    },
    de: {
      submitDocument: 'Dokument abschicken',
      submitQuestionnaire: 'Fragebogen absenden',
      submitDocumentDescription:
        'Möchten Sie das Dokument abschicken? Dieser Vorgang kann nicht rückgängig gemacht werden.',
      submitQuestionnaireDescription:
        'Möchten Sie den Fragebogen nun absenden? Nach dem Absenden können Sie Ihre Angaben nicht mehr ändern.',
      cancel: 'Abbrechen',
      yesSubmit: 'Ja, absenden',
      warning: 'Achtung',
      incompleteWarning: 'Sie haben noch nicht alle Fragen ausgefüllt.'
    }
  })

  const props = defineProps({
    type: {
      type: String as () => 'document' | 'questionnaire',
      required: true
    },
    isIncomplete: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    mobileBottomSheet: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits<{
    confirm: []
    cancel: []
  }>()

  const title = computed(() => {
    return props.type === 'questionnaire' ? t('submitQuestionnaire') : t('submitDocument')
  })

  const description = computed(() => {
    return props.type === 'questionnaire'
      ? t('submitQuestionnaireDescription')
      : t('submitDocumentDescription')
  })

  const buttonText = computed(() => {
    if (props.type === 'questionnaire' && props.isIncomplete) {
      return t('submitQuestionnaire') + ' ' + t('warning').toLowerCase()
    }
    return props.type === 'questionnaire' ? t('submitQuestionnaire') : t('submitDocument')
  })

  const handleConfirm = () => {
    emit('confirm')
  }

  const handleCancel = () => {
    emit('cancel')
  }
</script>

<template>
  <AlertDialog>
    <AlertDialogTrigger as-child>
      <slot name="trigger">
        <Button
          :class="
            isIncomplete
              ? 'bg-red-600 hover:bg-red-700'
              : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
          "
          :disabled="disabled"
        >
          {{ buttonText }}
        </Button>
      </slot>
    </AlertDialogTrigger>
    <AlertDialogContent :mobile-bottom-sheet="props.mobileBottomSheet">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ title }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ description }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="handleCancel">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          :class="
            isIncomplete
              ? 'bg-red-600 hover:bg-red-700'
              : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
          "
          :disabled="disabled"
          @click="handleConfirm"
        >
          {{ t('yesSubmit') }}
          <Loader2
            v-if="disabled"
            class="ml-2 h-4 w-4 animate-spin"
          />
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
